import { FC, createContext, useCallback, useContext, useState } from 'react';
import * as React from 'react';
import omitBy from 'lodash/fp/omitBy';
import noop from 'lodash/noop';

import {
  CHANGE_DATE_REQUEST_STAGES as stages,
  MODAL_HEADER_ON_CHANGE_DATE_REQUEST_FAILURE,
  MODAL_BODY_ON_CHANGE_DATE_REQUEST_FAILURE,
} from 'config/constants';
import { useCreateDateAmendmentRequestMutation } from 'lib/graphql';
import { useActions } from 'lib/hooks/useActions';
import { useDetailsBooking } from 'lib/hooks/useDetailsBooking';
import { modalCloseButton as trackModalCloseButton } from 'lib/gaTracking/crossPageEvents';
import { ChangeDatesRequestFlowContextValue } from './types';
import { ApolloError } from 'apollo-boost';

const omitEmptyValues = omitBy((value) => !value);

const initialFormValue = {
  checkIn: '',
  checkOut: '',
  phoneNumber: '',
  email: '',
};

export const ChangeDatesRequestFlowContext = createContext<ChangeDatesRequestFlowContextValue>({
  goToBookingDetails: noop,
  booking: undefined,
  stage: stages.EDIT,
  initialValues: initialFormValue,
  originalValues: initialFormValue,
  onSubmitDraft: noop,
  serverError: undefined,
  values: initialFormValue,
  backToForm: noop,
  createDateAmendmentRequestMutation: noop,
  loading: false,
  caseNumber: undefined,
});

const ChangeDatesRequestFlowContextProvider: FC = ({ children }) => {
  const { navigation, modal } = useActions();
  const { booking } = useDetailsBooking();
  const [stage, setStage] = useState(stages.EDIT);
  const [values, setValues] = useState(initialFormValue);
  const [serverError, setServerError] = useState<ApolloError>();
  const onSubmitDraft = useCallback(
    (val) => {
      setValues(val);
      setStage(stages.REVIEW);
    },
    [setValues, setStage],
  );
  const backToForm = useCallback(() => setStage(stages.EDIT), [setStage]);
  const goToBookingDetails = useCallback(() => navigation.goToBookingDetails({ bookingId: booking.id }), [navigation, booking]);

  const [createDateAmendmentRequestMutation, { loading, data }] = useCreateDateAmendmentRequestMutation({
    variables: {
      bookingId: booking.id,
      input: {
        checkIn: values.checkIn,
        checkOut: values.checkOut,
        phoneNumber: values.phoneNumber,
        email: values.email,
      },
    },
    onCompleted: () => setStage(stages.CONFIRMED),
    onError: (error) => {
      setServerError(error);
      modal.setupModal({
        variant: 'error',
        header: MODAL_HEADER_ON_CHANGE_DATE_REQUEST_FAILURE,
        body: MODAL_BODY_ON_CHANGE_DATE_REQUEST_FAILURE,
        onClose: trackModalCloseButton,
        isOpen: true,
      });
    },
  });

  const originalValues = {
    checkIn: booking.reservation.checkIn,
    checkOut: booking.reservation.checkOut,
    phoneNumber: booking.travelArranger.phone,
    email: booking.travelArranger.email,
  };

  const onCreateDateAmendmentRequestMutation = () => {
    setStage(stages.SUBMITTING);
    createDateAmendmentRequestMutation();
  };

  const contextValue = {
    goToBookingDetails,
    booking,
    stage,
    initialValues: { ...originalValues, ...omitEmptyValues(values) },
    originalValues,
    onSubmitDraft,
    serverError,
    values,
    backToForm,
    createDateAmendmentRequestMutation: onCreateDateAmendmentRequestMutation,
    loading,
    caseNumber: data?.createDateAmendmentRequest.caseNumber,
  };

  return <ChangeDatesRequestFlowContext.Provider value={contextValue}>{children}</ChangeDatesRequestFlowContext.Provider>;
};

// eslint-disable-next-line react/display-name
export const withChangeDatesRequestFlowContext = (Component: React.ComponentType) => () =>
  (
    <ChangeDatesRequestFlowContextProvider>
      <Component />
    </ChangeDatesRequestFlowContextProvider>
  );

export const useChangeDatesRequestFlowContext = () => useContext(ChangeDatesRequestFlowContext);
